<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconFolderPlus from '@/components/icon/IconFolderPlus.vue'
import IconFolderTab from '@/components/icon/IconFolderTab.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import PlayoutFilesDirectoryRow from './PlayoutFilesDirectoryRow.vue'

const props = defineProps({
	playoutId: {
		type: String,
		default: null,
	},
	uploadDirs: {
		type: Array,
		default: () => [],
	},
	loadingUploadDirs: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['open-upload-modal', 'create-folder', 'add-file'])

const store = useStore()

const showNewFolder = ref(false)
const newFolderName = ref('')

const vodStreams = computed(() => store.getters['Streams/vodStreams'] || [])

const maxFilesNum = computed(() => store.getters['User/tvPlayoutMaxUploadFilesNum'] || 0)

const filesNumReached = computed(
	() => maxFilesNum.value > 0 && uploadedFilesCount.value >= maxFilesNum.value,
)

const uploadButtonTooltip = computed(() => {
	if (!hasUploadDir.value) return 'Create a folder before uploading files'
	if (filesNumReached.value) {
		return `You've reached the maximum of ${maxFilesNum.value} files. Remove some files or upgrade your plan to upload more.`
	}
	return ''
})

const isUploadDisabled = computed(() => !hasUploadDir.value || filesNumReached.value)

// Only show live-recording folders that actually have files — empty ones just
// clutter the list (unlike user upload folders, which must stay visible so
// newly created dirs can receive uploads).
const liveToVodFolders = computed(() =>
	vodStreams.value.filter((stream) => stream.liveParent && (stream.totalFiles ?? 0) > 0),
)

const playoutDirs = computed(() => {
	const list = props.uploadDirs || []
	return [...list].reverse()
})

const allDirectories = computed(() => {
	const playoutEntries = playoutDirs.value.map((dir) => ({
		key: 'p-' + (dir.id || dir._id || dir.name),
		dir,
		isLiveToVod: false,
	}))
	const liveEntries = liveToVodFolders.value.map((dir) => ({
		key: 'l-' + (dir._id || dir.id || dir.name),
		dir,
		isLiveToVod: true,
	}))
	return [...playoutEntries, ...liveEntries]
})

const hasDirectories = computed(() => allDirectories.value.length > 0)
const hasUploadDir = computed(() => playoutDirs.value.length > 0)

// Live file counts reported by each directory row (keyed by dir id). This is
// the source of truth for the summary because the upload-dirs listing does not
// carry per-directory file counts.
const dirFileCounts = ref({})
const onDirFilesCount = ({ id, count }) => {
	if (id == null) return
	dirFileCounts.value[id] = count
}

const dirCountKey = (dir) => dir?.id || dir?._id || dir?.name

const totalFilesCount = computed(() => {
	let count = 0
	for (const entry of allDirectories.value) {
		const id = dirCountKey(entry.dir)
		if (id != null && id in dirFileCounts.value) {
			count += dirFileCounts.value[id]
		} else if (entry.isLiveToVod) {
			count += entry.dir?.totalFiles ?? 0
		} else if (Array.isArray(entry.dir?.files)) {
			count += entry.dir.files.length
		} else if (typeof entry.dir?.totalFiles === 'number') {
			count += entry.dir.totalFiles
		}
	}
	return count
})

// Files that count against the upload limit — only files inside playout upload
// directories (live-to-vod recordings are not uploads and don't count).
const uploadedFilesCount = computed(() => {
	let count = 0
	for (const entry of allDirectories.value) {
		if (entry.isLiveToVod) continue
		const id = dirCountKey(entry.dir)
		if (id != null && id in dirFileCounts.value) {
			count += dirFileCounts.value[id]
		} else if (Array.isArray(entry.dir?.files)) {
			count += entry.dir.files.length
		} else if (typeof entry.dir?.totalFiles === 'number') {
			count += entry.dir.totalFiles
		}
	}
	return count
})

const directoriesCount = computed(() => allDirectories.value.length)

const filesSummary = computed(() => {
	const dirs = directoriesCount.value
	const files = totalFilesCount.value
	const filesLabel = `${files} ${files === 1 ? 'file' : 'files'}`
	const dirsLabel = `${dirs} ${dirs === 1 ? 'directory' : 'directories'}`
	return `${filesLabel} in ${dirsLabel}`
})

const toggleNewFolder = () => {
	showNewFolder.value = !showNewFolder.value
	if (!showNewFolder.value) newFolderName.value = ''
}

const confirmNewFolder = () => {
	const name = newFolderName.value.trim()
	if (!name) {
		showNewFolder.value = false
		return
	}
	emit('create-folder', name)
	newFolderName.value = ''
}

watch(
	() => props.uploadDirs,
	() => {
		if (showNewFolder.value) {
			showNewFolder.value = false
			newFolderName.value = ''
		}
	},
)

const openUploadModal = () => {
	if (!hasUploadDir.value || filesNumReached.value) return
	emit('open-upload-modal')
}

const onAddFile = (file) => emit('add-file', file)
</script>

<template>
	<section class="flex flex-col gap-3 min-w-0 max-w-full">
		<div class="flex flex-wrap items-center gap-3">
			<div
				class="flex items-center shrink-0 rounded-md border border-surface-2 text-xs"
				style="border-color: var(--surface-2) !important;"
			>
				<div class="text-center px-3 py-2 min-w-[120px] whitespace-nowrap text-white">
					<span :class="filesNumReached ? 'text-error' : ''">{{ uploadedFilesCount }}</span> / {{ maxFilesNum }} files
				</div>
			</div>

			<span class="text-tiny text-surface-7">{{ filesSummary }}</span>

			<div class="w-full md:w-auto md:ml-auto flex items-center gap-2">
				<Tooltip
					:content="uploadButtonTooltip"
					:active="!!uploadButtonTooltip"
					wrapperClass="basis-1/2 md:basis-auto min-w-0 [&>.v-popper]:w-full"
					popperClass="max-w-[260px] text-sm"
				>
					<FormButton class="!w-full justify-center md:!w-auto" :disabled="isUploadDisabled" @click="openUploadModal">
						<icon-base class="mr-2">
							<icon-plus />
						</icon-base>
						Upload file
					</FormButton>
				</Tooltip>
				<FormButton
					type="secondary"
					class="basis-1/2 md:basis-auto !w-full justify-center md:!w-auto min-w-0"
					@click="toggleNewFolder"
				>
					<icon-base class="mr-2 shrink-0" :width="16" :height="16" width-view-box="24" height-view-box="24" fill="none">
						<icon-folder-plus />
					</icon-base>
					New folder
				</FormButton>
			</div>
		</div>

		<div
			v-if="showNewFolder"
			class="flex items-center gap-2 px-3 py-2 rounded-md bg-surface-2"
		>
			<icon-base class="shrink-0 text-surface-9" :width="15" :height="13" width-view-box="15" height-view-box="13"
				fill="none">
				<icon-folder-tab />
			</icon-base>
			<input
				v-model="newFolderName"
				type="text"
				placeholder="New folder name"
				class="bg-transparent text-sm text-white placeholder-surface-6 outline-none w-full border-none focus:ring-0"
				@keyup.enter="confirmNewFolder"
				@keyup.esc="toggleNewFolder"
				autofocus
			/>
			<FormButton type="link" size="sm" @click="toggleNewFolder">Cancel</FormButton>
			<FormButton size="sm" :disabled="!newFolderName.trim()" @click="confirmNewFolder">
				Create
			</FormButton>
		</div>

		<div v-if="loadingUploadDirs && !hasDirectories" class="text-tiny text-surface-7 py-4 text-center">
			Loading directories…
		</div>
		<div
			v-else-if="!hasDirectories"
			class="bg-surface-2 rounded-lg p-8 text-center"
		>
			<p class="text-sm text-surface-7 m-0">No files yet, Create a folder to get started</p>
		</div>
		<ul v-else class="bg-surface-2 rounded-lg px-4 divide-y divide-surface-3 min-w-0 max-w-full overflow-hidden">
			<li v-for="entry in allDirectories" :key="entry.key" class="min-w-0">
				<PlayoutFilesDirectoryRow
					:dir="entry.dir"
					:playout-id="playoutId"
					:is-live-to-vod="entry.isLiveToVod"
					@add-file="onAddFile"
					@files-count="onDirFilesCount"
				/>
			</li>
		</ul>
	</section>
</template>

<style scoped></style>
